import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdAccounts, useTriggerBackfill } from "@/hooks/useAdPerformance";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  History,
} from "lucide-react";

export function AccountHealthTab() {
  const { data: accounts, isLoading } = useAdAccounts();
  const [open, setOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [metaAccountId, setMetaAccountId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [backfillDays, setBackfillDays] = useState("30");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const backfill = useTriggerBackfill();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedId = metaAccountId.trim();
    const trimmedName = accountName.trim();
    if (!trimmedId || !trimmedName) return;

    setSubmitting(true);
    const { error } = await supabase.from("ad_accounts").insert({
      meta_account_id: trimmedId,
      meta_account_name: trimmedName,
      is_active: true,
      sync_status: "pending",
    });
    setSubmitting(false);

    if (error) {
      toast({
        title: "Failed to add account",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Account added", description: `${trimmedName} (${trimmedId}) is ready to sync.` });
    queryClient.invalidateQueries({ queryKey: ["ad_accounts"] });
    setMetaAccountId("");
    setAccountName("");
    setOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleBackfill = async (e: React.FormEvent) => {
    e.preventDefault();
    const days = parseInt(backfillDays, 10);
    if (isNaN(days) || days < 1 || days > 90) return;
    const until = new Date().toISOString().slice(0, 10);
    const since = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    try {
      await backfill.mutateAsync({ since, until });
      toast({ title: "Backfill started", description: `Syncing ${days} days of historical data (${since} → ${until}).` });
      setBackfillOpen(false);
    } catch {
      toast({ title: "Backfill failed", description: "Could not trigger backfill. Check Edge Function logs.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <History className="h-4 w-4 mr-1" />
              Backfill
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Backfill Historical Data</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBackfill} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="backfill-days">Days to backfill</Label>
                <Input
                  id="backfill-days"
                  type="number"
                  min={1}
                  max={90}
                  value={backfillDays}
                  onChange={(e) => setBackfillDays(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Syncs performance data for the last N days (max 90).
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={backfill.isPending}>
                {backfill.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Start Backfill
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Meta Ad Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="meta-account-id">Meta Account ID</Label>
                <Input
                  id="meta-account-id"
                  placeholder="123456789"
                  value={metaAccountId}
                  onChange={(e) => setMetaAccountId(e.target.value.replace(/\D/g, ""))}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Numeric ID only — without the <code>act_</code> prefix.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-name">Account Name</Label>
                <Input
                  id="account-name"
                  placeholder="TJ - Life Insurance"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Add Account
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {(!accounts || accounts.length === 0) ? (
        <div className="text-center py-20 text-muted-foreground">
          <p>No ad accounts connected. Add your Meta ad account to get started.</p>
        </div>
      ) : (
        accounts.map((account) => (
          <Card key={account.id}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{account.meta_account_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {account.meta_account_id}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className={
                      account.is_active
                        ? "border-green-500 text-green-700"
                        : "border-red-500 text-red-700"
                    }
                  >
                    {account.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {account.sync_status === "synced" && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Synced
                    </div>
                  )}
                  {account.sync_status === "syncing" && (
                    <div className="flex items-center gap-1 text-xs text-blue-600">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Syncing
                    </div>
                  )}
                  {account.sync_status === "error" && (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Error
                    </div>
                  )}
                  {account.sync_status === "pending" && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      Pending
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                <span>Currency: {account.currency}</span>
                <span>Timezone: {account.timezone}</span>
                {account.last_synced_at && (
                  <span>
                    Last sync: {new Date(account.last_synced_at).toLocaleString()}
                  </span>
                )}
              </div>
              {account.sync_error && (
                <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                  {account.sync_error}
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
